import { ReactNode, useCallback, useEffect } from 'react';

import { Dialog } from '@headlessui/react';
import { useSpring, animated, config } from '@react-spring/web';
import { useDrag } from '@use-gesture/react';

import { ModalOverlay } from '..';
import { ModalVariant } from '../custom-modals';
import { cn } from '@restworld/utils-common';

export interface DrawerLayoutProps {
  variant?: ModalVariant;

  children: ReactNode;

  /**
   * Whether is open
   */
  isOpen: boolean;

  /**
   * Modal props only used ONLY when layout == modal
   */
  styles?: React.CSSProperties;

  width?: number;

  /**
   * Configure from where to open the drawer
   */
  position?: 'left' | 'right';

  /**
   * Callback when to close it
   */
  onClose: () => void;
}

const AnimatedDrawer = animated(Dialog);

export function DrawerLayout({
  isOpen,
  children,
  position,
  styles,
  variant = 'tertiary',
  width = 400,
  onClose,
}: DrawerLayoutProps) {
  useEffect(() => {
    // 👇 add class to body element
    if (isOpen) {
      document.body.classList.add('dialog-open');
    } else {
      document.body.classList.remove('dialog-open');
    }
  }, [isOpen]);

  const drawerWidth: number = width;
  const [{ x }, api] = useSpring(() => ({ x: width }));

  const openDrawer = useCallback(
    ({ canceled }: { canceled?: boolean }) => {
      // when cancel is true, it means that the user passed the upwards threshold
      // so we change the spring config to create a nice wobbly effect
      api.start({
        x: 0,
        immediate: false,
        config: canceled ? config.wobbly : config.stiff,
      });
    },
    [api]
  );

  const closeDrawer = useCallback(
    (velocity = 0) => {
      api.start({
        x: width,
        immediate: false,
        config: { ...config.stiff, velocity },
      });
      //eslint-disable-next-line @typescript-eslint/ban-types
      //eslint-disable-next-line no-restricted-globals
      close();
    },
    [api, width]
  );

  useEffect(() => {
    if (isOpen) {
      openDrawer({ canceled: false });
    }
  }, [isOpen, openDrawer]);

  const display = x.to((px) => (px < width ? 'flex' : 'none'));

  const bindDrag = useDrag(
    ({ last, velocity: [vx], movement: [mx], direction: [xDir], canceled }) => {
      // when the user releases the sheet, we check whether it passed
      // the threshold for it to close, or if we reset it to its open positino
      if (last) {
        mx > width * 0.5 || (vx > 0.5 && xDir > 0) ? closeDrawer(vx) : openDrawer({ canceled });
      }
      // when the user keeps dragging, we just move the sheet according to
      // the cursor position
      else api.start({ x: mx, immediate: true });
    },
    {
      from: () => [x.get(), 0],
    }
  );

  return isOpen ? (
    <>
      <ModalOverlay show={isOpen} onClick={onClose} />
      <StyledDrawer
        variant={variant}
        open={isOpen}
        onClose={onClose}
        drawerPosition={position}
        {...bindDrag()}
        style={{
          width: drawerWidth,
          display,
          x,
        }}
      >
        {children}
      </StyledDrawer>
    </>
  ) : null;
}

const commonStyle = 'max-w-[26rem] top-[32.667%]';

const variantStyles: Record<ModalVariant, string> = {
  primary: commonStyle,
  secondary: `h-1/2 overflow-y-auto ${commonStyle}`,
  tertiary: 'max-w-[26rem]',
};

const StyledDrawer = ({ variant, drawerPosition, className, ...props }: any) => (
  <AnimatedDrawer
    className={cn(
      variantStyles[variant as ModalVariant],
      'touch-none items-center max-w-none fixed top-0 z-[160] h-screen',
      drawerPosition === 'left' ? 'left-0' : 'right-0',
      'flex flex-col w-[100vw] md:h-full sm:bottom-0 xs:bottom-0 rounded-t-3xl shadow bg-white overflow-y-auto',
      '[&>div:nth-child(2)]:md:h-full [&>div:nth-child(2)]:md:px-[32px]',
      '[&>div:nth-child(2)>div:nth-child(2)]:rounded-none [&>div:nth-child(2)>div:nth-child(2)]:shadow-none [&>div:nth-child(2)>div:nth-child(2)]:bg-none',
      className
    )}
    {...props}
  />
);

export default DrawerLayout;
